import { useState } from "react";
import { Box } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import { common, grey } from "@mui/material/colors";
import {
  DECLINE_RED,
  GAIN_GREEN,
  LINK_BLUE,
  PURPLE_TINT,
  QVENTUS_PURPLE,
  QVENTUS_PURPLE_TEXT,
} from "./malnutrition-colors";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC, ReactNode } from "react";
import type {
  PatientWeight,
  PatientWeightHistory,
} from "../../types/Malnutrition";

const styles = {
  subCard: css`
    background: ${grey[50]};
    border-radius: 0.5rem;
    margin: 0 0.75rem 0.625rem;
    padding: 0.25rem 1rem 0.25rem 1rem;
  `,
  tableHead: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.5rem 0;
    border-bottom: 1px solid ${grey[200]};
    color: ${grey[700]};
    font-weight: 600;
    font-size: 0.8125rem;
  `,
  tableRow: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.5rem 0;
    font-size: 0.875rem;
    color: ${grey[800]};
    align-items: center;
  `,
  anchorRow: css`
    background: ${PURPLE_TINT};
    margin: 0 -0.75rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    position: relative;
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.25rem;
      bottom: 0.25rem;
      width: 3px;
      background: ${QVENTUS_PURPLE};
      border-radius: 1px;
    }
  `,
  rangeSelector: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: ${grey[700]};
    font-size: 0.8125rem;
  `,
  // Colour comes from trendTone via sx, not here — it depends on the direction.
  trendLine: css`
    font-weight: 700;
    font-size: 0.9375rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  `,
  sourceCell: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-width: 0;
  `,
  // min-width:0 on the flex parent isn't enough — the text itself keeps its
  // min-content width unless told otherwise, and at the card's 20rem minimum
  // "Outpatient" plus the pill is wider than the third column.
  sourceText: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  `,
};

const sxStyles = {
  subHeading: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: grey[700],
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    display: "flex",
    alignItems: "center",
  },
  // Names what the purple tint on a row means, so the anchors aren't carried
  // by colour alone.
  // Via sx, not an emotion class: MuiLink's own class wins the cascade against
  // a plain className, which silently left the link the theme's purple.
  showMore: {
    display: "inline-block",
    marginTop: "0.5rem",
    fontSize: "0.875rem",
    color: LINK_BLUE,
    textDecorationColor: LINK_BLUE,
  },
  anchorPill: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 0.375rem",
    borderRadius: "999px",
    border: `1px solid ${QVENTUS_PURPLE}`,
    background: common.white,
    color: QVENTUS_PURPLE_TEXT,
    fontSize: "0.6875rem",
    fontWeight: 600,
    lineHeight: 1.6,
    whiteSpace: "nowrap",
  },
};

interface WeightTrend {
  percent: number;
  spanMs: number;
}

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const DAYS_PER_WEEK = 7;
// EBA's month (see _build_recent_weights in events_based_arch's
// malnutrition_decision.py).
const DAYS_PER_MONTH = 30;

// The model won't compare two weights taken less than 96h apart (dsModels'
// default StructuredThresholds.min_comparison_gap_hours, DS-9912), so neither
// does the card (ENG-31026). Keep the two equal.
const MIN_TREND_SPAN_MS = 96 * MS_PER_HOUR;

// One decimal, as EBA documents the trend ("-7.3% over ~0.6 months").
const oneDecimal = (n: number): number => Math.round(n * 10) / 10;

const plural = (n: number, unit: string): string =>
  `${n} ${unit}${n === 1 ? "" : "s"}`;

// The unit is picked on whole days, so 6.9 days reads "~1 week" rather than
// "~7 days".
const spanText = (ms: number): string => {
  const days = Math.round(ms / MS_PER_DAY);
  if (days < DAYS_PER_WEEK) return `~${plural(days, "day")}`;
  if (days < DAYS_PER_MONTH) {
    return `~${plural(oneDecimal(days / DAYS_PER_WEEK), "week")}`;
  }
  return `~${plural(oneDecimal(days / DAYS_PER_MONTH), "month")}`;
};

// The change from the FROM reading to the TO reading, by EBA's formula. EBA's
// trend_percent / trend_months cover its oldest and newest readings, which are
// the default selection, so the untouched card shows EBA's percentage and a
// re-pick recomputes it. Null when there's no baseline weight to divide by, or
// the readings are too close together to compare.
const trendBetween = (
  from: PatientWeight,
  to: PatientWeight
): WeightTrend | null => {
  const spanMs = Date.parse(to.reading_ts) - Date.parse(from.reading_ts);
  if (!(from.weight_lb > 0) || !(spanMs >= MIN_TREND_SPAN_MS)) return null;
  return {
    percent: oneDecimal(
      ((to.weight_lb - from.weight_lb) / from.weight_lb) * 100
    ),
    spanMs,
  };
};

// The arrow and colour follow the direction: down and red for a decline, up and
// green for a gain, neutral with no arrow for no change. Judged on the rounded
// figure, so a change shown as 0% asserts no direction.
const trendTone = (percent: number) => {
  if (percent < 0) return { color: DECLINE_RED, Icon: ArrowDownwardIcon };
  if (percent > 0) return { color: GAIN_GREEN, Icon: ArrowUpwardIcon };
  return { color: grey[800], Icon: null };
};

const trendText = ({ percent, spanMs }: WeightTrend): string =>
  `${percent}% over ${spanText(spanMs)}`;

const Trend: FC<{ trend: WeightTrend }> = ({ trend }) => {
  const { color, Icon } = trendTone(trend.percent);
  return (
    <Box className={styles.trendLine} sx={{ color }}>
      {Icon && <Icon sx={{ fontSize: "1rem" }} />}
      {trendText(trend)}
    </Box>
  );
};

const TrendChip: FC<{ trend: WeightTrend }> = ({ trend }) => {
  const { color, Icon } = trendTone(trend.percent);
  return (
    <Box
      component="span"
      sx={{
        color,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
      }}
    >
      {Icon && <Icon sx={{ fontSize: "0.9rem" }} />}
      {trendText(trend)}
    </Box>
  );
};

interface RecentWeightsCardProps {
  history: PatientWeightHistory;
}

// A reading's identity for endpoint selection. The API gives readings no id,
// and the weights query polls — a poll that adds a reading (they arrive
// newest-first) or ages one out shifts every position. The backend can't help
// either: readings are deleted and recreated whenever any of them changes, so
// their pks don't survive an update. Content does. The occurrence suffix only
// separates exact duplicates, which are indistinguishable anyway.
const readingKeys = (readings: PatientWeight[]): string[] => {
  const seen = new Map<string, number>();
  return readings.map((r) => {
    const base = `${r.reading_ts}|${r.weight_lb}|${r.source}`;
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return `${base}#${occurrence}`;
  });
};

const RecentWeightsCard: FC<RecentWeightsCardProps> = ({ history }) => {
  // null = follow the default, the full range (oldest -> newest; readings arrive
  // newest-first), so a poll that brings a new reading moves the default with
  // it. Picking an endpoint pins it to that reading's key, which survives polls
  // and falls back to the default only if the reading drops out of the data —
  // never past the end of the array.
  const [fromKey, setFromKey] = useState<string | null>(null);
  const [toKey, setToKey] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  // A poll can empty the readings under a mounted card.
  if (!history.readings.length) return null;

  const keys = readingKeys(history.readings);
  const resolve = (key: string | null, fallback: number): number => {
    const idx = key === null ? -1 : keys.indexOf(key);
    return idx >= 0 ? idx : fallback;
  };
  const fromIndex = resolve(fromKey, keys.length - 1);
  const toIndex = resolve(toKey, 0);
  const readingAt = (idx: number) => history.readings[idx];
  const trend = trendBetween(readingAt(fromIndex), readingAt(toIndex));
  const timeOf = (idx: number) => Date.parse(readingAt(idx).reading_ts);
  // The default pair spans every reading, so if it's too close to compare,
  // every pair is: there's no range to pick and no ends to mark.
  const comparable = timeOf(0) - timeOf(keys.length - 1) >= MIN_TREND_SPAN_MS;
  const ends = comparable ? [fromIndex, toIndex] : [];

  const previewCount = 6;
  const allIndexes = history.readings.map((_, idx) => idx);
  // The preview, plus either endpoint that falls outside it: the endpoints
  // carry the Baseline/Latest pills, and a comparison whose ends aren't on
  // screen can't be read.
  const visibleIndexes = showAll
    ? allIndexes
    : Array.from(new Set([...allIndexes.slice(0, previewCount), ...ends])).sort(
        (a, b) => a - b
      );
  const hiddenCount = history.readings.length - visibleIndexes.length;

  return (
    <CollapsibleSection
      defaultOpen
      hideSummaryWhenOpen
      title="RECENT WEIGHTS"
      summary={trend && <TrendChip trend={trend} />}
      headingTextSx={sxStyles.subHeading}
      outerClassName={styles.subCard}
    >
      {trend && <Trend trend={trend} />}
      {comparable && (
        <Box className={styles.rangeSelector}>
          <span>FROM</span>
          <Select
            size="small"
            value={keys[fromIndex]}
            onChange={(e) => setFromKey(String(e.target.value))}
            sx={{ fontSize: "0.8125rem", height: "1.75rem" }}
          >
            {/* FROM stays at least MIN_TREND_SPAN_MS before TO. */}
            {history.readings.map((r, idx) => (
              <MenuItem
                key={keys[idx]}
                value={keys[idx]}
                disabled={timeOf(idx) > timeOf(toIndex) - MIN_TREND_SPAN_MS}
                sx={{ fontSize: "0.8125rem" }}
              >
                {r.weight_lb} lb · {r.date}
              </MenuItem>
            ))}
          </Select>
          <ArrowForwardIcon sx={{ fontSize: "0.875rem" }} />
          <span>TO</span>
          <Select
            size="small"
            value={keys[toIndex]}
            onChange={(e) => setToKey(String(e.target.value))}
            sx={{ fontSize: "0.8125rem", height: "1.75rem" }}
          >
            {history.readings.map((r, idx) => (
              <MenuItem
                key={keys[idx]}
                value={keys[idx]}
                disabled={timeOf(idx) < timeOf(fromIndex) + MIN_TREND_SPAN_MS}
                sx={{ fontSize: "0.8125rem" }}
              >
                {r.weight_lb} lb · {r.date}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      <Box className={styles.tableHead}>
        <span>Date</span>
        <span>Weight</span>
        <span>Source</span>
      </Box>
      {visibleIndexes.map((idx) => {
        const r = history.readings[idx];
        // The two ends the trend is computed between.
        const anchorLabel = !ends.includes(idx)
          ? null
          : idx === toIndex
          ? "Latest"
          : "Baseline";
        return (
          <Box
            key={keys[idx]}
            className={`${styles.tableRow}${
              anchorLabel ? " " + styles.anchorRow : ""
            }`}
          >
            <span>{r.date}</span>
            <span>{r.weight_lb} lb</span>
            <Box className={styles.sourceCell}>
              <span className={styles.sourceText}>{r.source}</span>
              {anchorLabel && (
                <Box component="span" sx={sxStyles.anchorPill}>
                  {anchorLabel}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
      {!showAll && hiddenCount > 0 && (
        <Link
          component="button"
          underline="hover"
          onClick={() => setShowAll(true)}
          sx={sxStyles.showMore}
        >
          Show {hiddenCount} more
        </Link>
      )}
    </CollapsibleSection>
  );
};

interface SupportingDataSectionProps {
  weights: PatientWeightHistory;
}

const SupportingDataHeader: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontSize: "0.8125rem",
      fontWeight: 700,
      color: grey[700],
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      padding: "0.75rem 1.5rem 0.5rem",
    }}
  >
    {children}
  </Box>
);

const SupportingDataSection: FC<SupportingDataSectionProps> = ({ weights }) => {
  // Nothing to show if there are no readings (e.g. all filtered out server-side);
  // avoid an empty card with two blank selects and an empty table.
  if (!weights.readings.length) return null;
  return (
    <Box>
      <SupportingDataHeader>Supporting clinical data</SupportingDataHeader>
      <RecentWeightsCard history={weights} />
    </Box>
  );
};

export default SupportingDataSection;
export { RecentWeightsCard };
